import { ScrollViewStyles } from '@unistocks/components/basic/scrollview';
import { Column, Row } from '@unistocks/components/layout/flex';
import { Loader } from '@unistocks/components/loading/spinner/loader';
import { Page } from '@unistocks/components/page/page';
import { AssetEvents } from '@unistocks/features/assets/components/asset-events';
import { AssetImage } from '@unistocks/features/assets/components/asset-image';
import { AssetSummary } from '@unistocks/features/assets/components/asset-summary';
import { useHistoryBreadcrumb } from '@unistocks/features/navigation/use-history-breadcrumb';
import { assetsGetQueryKey, useAssetsGetQuery } from '@unistocks/proxy/axios-client/AssetsQuery';
import { t } from '@unistocks/shared/localization/index.ts';
import { HasId } from '@unistocks/types/has-id';
import React, { Suspense } from 'react';

const AssetChart = React.lazy(
  () => import('@unistocks/features/assets/components/asset-chart/asset-chart')
);

export const AssetDetails: React.FC<HasId> = ({ id }) => {
  const { data: asset, isSuccess, isRefetching, refetch } = useAssetsGetQuery(id);
  const breadcrumbs = useHistoryBreadcrumb();

  return (
    <Page
      queryState={{
        queryKeys: [assetsGetQueryKey(id)],
        loadingText: t('::AssetManagement:Loading')
      }}
    >
      <Page.ScrollView
        refreshing={isRefetching}
        onRefresh={refetch}
        headerTitle={
          isSuccess ? (
            <Row gap="$2" alignItems="center">
              <AssetImage asset={asset} preferAssetImage size="$2.5" />
              <Page.Heading>{asset.name}</Page.Heading>
            </Row>
          ) : null
        }
        breadcrumbs={breadcrumbs}
        contentContainerStyle={ScrollViewStyles.contentContainerStyle}
      >
        {isSuccess ? (
          <Column gap="$3">
            <Page.Content>
              <Suspense fallback={<Loader />}>
                <AssetChart
                  asset={asset}
                  chartContainerProps={{
                    h: 400,
                    $sm: { h: 400 },
                    $md: { h: 500 }
                  }}
                />
              </Suspense>
            </Page.Content>
            <Row gap="$3" fd="column" ai="stretch" $md={{ fd: 'row', ai: 'flex-start' }}>
              <AssetSummary asset={asset} $md={{ maxW: '50%', flex: 1 }} />
              <AssetEvents asset={asset} $md={{ maxW: '50%', flex: 1 }} />
            </Row>
          </Column>
        ) : null}
      </Page.ScrollView>
    </Page>
  );
};

AssetDetails.displayName = 'AssetDetails';
